<template>
    <form
        ref="paymentForm"
        method="post"
        :action="formAction"
        @submit="onSubmit($event)"
        class="order-payment-container"
    >
        <GiftVoucherPaymentModal :order-hash="this.order.hash" :order-price="order.unpaid_amount" ref="giftVoucherPaymentModal" />

        <input type="hidden" name="_token" :value="csrfToken" />
        <input type="hidden" name="subtype" :value="subMethod" />

        <div v-if="!isWaitingForConfirmation" class="row mb-5">
            <template v-for="paymentMethod in paymentMethods">
                <div
                    :key="paymentMethod.id"
                    v-if="paymentMethod.id === 'paypal'"
                    class="col-md-6 no-mobile-padding"
                    @click="onPaymentOptionClick($event, paymentMethod)"
                    @touch="onPaymentOptionClick($event, paymentMethod)"
                >
                    <div class="cursor-pointer paypal-payment-container">
                        <div
                            ref="paypalButtons"
                            id="paypal-plus-container"
                            class="paypal-fixed-height"
                        >
                            <div class="payment-method paypal d-flex justify-content-between align-items-center">
                                <label class="font-weight-bold mt-2">
                                    {{ $t('shop.paypal') }}
                                    <span
                                        v-if="
                                            typeof paymentMethod.usageFee === 'number' && paymentMethod.usageFee !== 0
                                        "
                                    >
                                        {{
                                            $t('shop.paymentFee', {
                                                fee: paymentMethod.usageFee.toFixed(2),
                                            })
                                        }}
                                    </span>
                                </label>
                                <i class="payment-icon pf pf-paypal"></i>
                            </div>
                        </div>
                    </div>

                    <div class="form-group" v-show="showCountryDropdown">
                        <label for="payerCountry" v-html="$t('order.payerCountry')"></label>
                        <select id="payerCountry" class="form-control" name="payerCountry" v-model="country">
                            <option :value="null">{{ $t('order.payerCountryPlaceholder') }}</option>
                            <option v-for="option in countries" :key="option.country_code" :value="option.country_code">
                                {{ $t('country.' + option.country_code) }}
                            </option>
                        </select>
                    </div>
                </div>

                <div
                    :key="paymentMethod.id"
                    v-if="paymentMethod.id === 'gift_voucher'"
                    class="col-md-6 no-mobile-padding cursor-pointer"
                    @click="onPaymentOptionClick(isOrderWithAutoRenew ? $event : null, paymentMethod)"
                    @touch="onPaymentOptionClick(isOrderWithAutoRenew ? $event : null, paymentMethod)"
                >
                    <div class="payment-method payment-method-container" :class="paymentMethod.id">
                        <label class="font-weight-bold mt-2">
                            {{ $t(paymentMethod.name) }}
                            <span v-if="typeof paymentMethod.usageFee === 'number' && paymentMethod.usageFee !== 0">
                                    {{ $t('shop.paymentFee', {
                                        fee: paymentMethod.usageFee.toFixed(2),
                                    }) }}
                                </span>
                        </label>
                        <i class="payment-icon payment-icon-image"><svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512"><!--! Font Awesome Free 6.4.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M190.5 68.8L225.3 128H224 152c-22.1 0-40-17.9-40-40s17.9-40 40-40h2.2c14.9 0 28.8 7.9 36.3 20.8zM64 88c0 14.4 3.5 28 9.6 40H32c-17.7 0-32 14.3-32 32v64c0 17.7 14.3 32 32 32H480c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H438.4c6.1-12 9.6-25.6 9.6-40c0-48.6-39.4-88-88-88h-2.2c-31.9 0-61.5 16.9-77.7 44.4L256 85.5l-24.1-41C215.7 16.9 186.1 0 154.2 0H152C103.4 0 64 39.4 64 88zm336 0c0 22.1-17.9 40-40 40H288h-1.3l34.8-59.2C329.1 55.9 342.9 48 357.8 48H360c22.1 0 40 17.9 40 40zM32 288V464c0 26.5 21.5 48 48 48H224V288H32zM288 512H432c26.5 0 48-21.5 48-48V288H288V512z"/></svg></i>
                    </div>
                </div>

                <template v-if="typeof paymentMethod.subtypes === 'object' && paymentMethod.subtypes !== null">
                    <div
                        v-for="(paymentMethodSubtype, subMethod) in paymentMethod.subtypes"
                        :key="paymentMethodSubtype.id"
                        class="col-md-6 no-mobile-padding cursor-pointer"
                        @click="onPaymentOptionClick($event, paymentMethod, subMethod)"
                        @touch="onPaymentOptionClick($event, paymentMethod, subMethod)"
                        v-show="(!isOrderWithAutoRenew || paymentMethodSubtype.supportsSubscription) && disablePaymentMethod !== paymentMethodSubtype.id"
                    >
                        <div class="payment-method" :class="paymentMethodSubtype.class || paymentMethodSubtype.id">
                            <label class="font-weight-bold mt-2">
                                {{ $t(paymentMethodSubtype.name) }}
                                <span v-if="typeof paymentMethod.usageFee === 'number' && paymentMethod.usageFee !== 0">
                                    {{
                                        $t('shop.paymentFee', {
                                            fee: paymentMethod.usageFee.toFixed(2),
                                        })
                                    }}
                                </span>
                            </label>
                            <i
                                class="payment-icon pf"
                                :class="paymentMethodSubtype.icon"
                                v-if="!paymentMethodSubtype.id.includes('klarna')"
                            ></i>
                            <img v-else src="/images/payment/klarna.svg" class="payment-icon klarna" alt="Klarna" />
                        </div>
                    </div>
                </template>
            </template>
            <template v-if="isOrderWithAutoRenew">
                <div class="col-md-6 offset-md-3 no-mobile-padding mt-2">
                    <button
                        class="btn btn-outline-primary w-100"
                        @click="
                            $event.preventDefault();
                            $emit('terminate-subscription');
                        "
                    >
                        {{ $t('shop.subscriptionMorePaymentMethods') }}
                    </button>
                </div>
            </template>
        </div>

        <icon-text-container v-if="!isVoucherOrder">
            <template v-slot:icon>
                <i v-if="isWaitingForConfirmation" class="mdi mdi-loading mdi-spin status-icon status-icon-warning"></i>
                <i v-else-if="isPaymentRefunded" class="mdi mdi-window-close status-icon status-icon-fail"></i>
                <i v-else class="mdi mdi-exclamation status-icon status-icon-warning"></i>
            </template>

            <template v-if="isWaitingForConfirmation">
                <h4
                    class="mb-0 mb-mobile mobile-text-center"
                    v-html="$t('subscription.waitingForConfirmationTitle')"
                ></h4>
                <span
                    class="text-quote mobile-text-center"
                    v-html="
                        $t('subscription.waitingForConfirmationText', {
                            timeLeft: waitingForConfirmationTime,
                        })
                    "
                ></span>
            </template>
            <template v-else-if="isPaymentRefunded">
                <h4 class="mb-0 mb-mobile mobile-text-center" v-html="$t('shop.attentionRefunded')"></h4>
                <span
                    class="text-quote mobile-text-center"
                    v-html="$t('order.payRefunded', { price: order.unpaid_amount.toFixed(2) })"
                ></span>
            </template>
            <template v-else>
                <h4 class="mb-0 mb-mobile mobile-text-center" v-html="$t('shop.attention')"></h4>
                <span
                    v-if="order.subscription_created"
                    class="text-quote mobile-text-center"
                    v-html="$t('order.subscriptionFollowUpOrderPleasePayIt', { price: order.unpaid_amount.toFixed(2) })"
                ></span>
                <span
                    v-else
                    class="text-quote mobile-text-center"
                    v-html="$t('order.please_pay_it', { price: order.unpaid_amount.toFixed(2) })"
                ></span>
            </template>
        </icon-text-container>

        <order-payment-billing-data-modal
            v-if="askForBillingData"
            :order="order"
            :countries="countries"
            :payment-method="askForBillingDataDetails.paymentMethod"
            :payment-subtype="askForBillingDataDetails.subtype"
            :update-order-invoice-url="orderInvoiceUpdateUrl"
            @aborted="askForBillingData = false"
            @confirmed="
                onPaymentOptionClick(
                    null,
                    askForBillingDataDetails.paymentMethod,
                    askForBillingDataDetails.subMethod,
                    true
                )
            "
        ></order-payment-billing-data-modal>

        <modal
            v-if="hasEnabledDelayNotice"
            id="payment-delay-notice-modal"
            :centered="true"
            :title="paymentNoticeModal.title"
            :show="paymentNoticeModal.show"
            :close="paymentNoticeModal.close"
            :closeable="paymentNoticeModal.closeable"
            size="modal-lg"
        >
            <div class="modal-body">
                <p class="mb-0" v-html="paymentNoticeModal.text"></p>
            </div>
            <div class="modal-footer justify-content-center">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="onDelayModalActionClick(false)"
                    v-html="$t('generic.no')"
                ></button>
                <button
                    type="button"
                    class="btn btn-primary"
                    @click="onDelayModalActionClick(true)"
                    v-html="$t('shop.continue')"
                ></button>
            </div>
        </modal>
    </form>
</template>

<script>
import GiftVoucherPaymentModal from './Payment/GiftVoucherPaymentModal.vue';

export default {
    components: {GiftVoucherPaymentModal},
    inject: ['isVoucherOrder'],
    props: {
        csrfToken: {
            type: String,
            required: true,
        },
        payment: {
            type: Object,
            required: false,
        },
        activePaymentMethod: {
            type: Boolean,
            required: false,
            default() {
                return null;
            },
        },
        paymentMethods: {
            type: Array,
            required: true,
            default() {
                return [
                    {
                        id: 'example',
                        name: 'Example',
                        icon: null,
                        enabled: false,
                        subtypes: {
                            example: {
                                enabled: false,
                                sort: 0,
                                id: 'example',
                                delayNotice: {
                                    disabled: false,
                                    title: 'shop.delayNoticeTitle',
                                    text: 'shop.delayNoticeText',
                                },
                                name: 'shop.example',
                                icon: 'pf-bank-transfer',
                            },
                        },
                    },
                ];
            },
        },
        order: {
            type: Object,
            required: true,
        },
        price: {
            type: Number,
            required: true,
        },
        isPerformingRequest: {
            type: Boolean,
            required: true,
        },
        countries: {
            type: Array,
            required: false,
        },
        defaultCountry: {
            type: String,
            required: false,
            default() {
                return null;
            },
        },
        showCountryDropdown: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        orderInvoiceUpdateUrl: {
            type: String,
            required: false,
            default() {
                return null;
            },
        },
        isWaitingForConfirmation: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        waitingForConfirmationTime: {
            type: String,
            required: false,
            default() {
                return '';
            },
        },
        guestOrders: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        disablePaymentMethod: {
            type: String,
            required: false,
        },
    },

    data() {
        return {
            method: null,
            subMethod: null,
            askForBillingData: false,
            askForBillingDataDetails: {
                paymentMethod: null,
                subtype: null,
                subMethod: null,
            },
            paymentNoticeModal: {
                show: false,
                close: false,
                closeable: false,
                title: '',
                text: '',
            },
            country: this.defaultCountry,
            payPalInitialized: false,
            requestOngoing: this.isPerformingRequest,
        };
    },

    computed: {
        /**
         * Define the url of form tag and async request to initialize the payment process.
         *
         * @returns {string}
         */
        formAction() {
            return this.generateFormActionLink(this.method);
        },

        /**
         * Determine if the payment were refunded by an chargeback.
         *
         * @returns {boolean}
         * */
        isPaymentRefunded() {
            return this.payment && (this.payment.was_refunded || this.payment.wasRefunded || false);
        },

        /**
         * Handle the search for enabled delay notice to show.
         *
         * @returns {boolean}
         * */
        hasEnabledDelayNotice() {
            // Step the payment method and check them only if they have a subtype definition.
            for (let i = 0; i < this.paymentMethods.length; i++) {
                // Read the payment and check if they contain a notice display.
                const payment = this.paymentMethods[i];

                // The payment method does not have a subtype definition, we can
                // skip this method. Because a check does not make much sense.
                if (typeof payment.subtypes !== 'object' || payment.subtypes === null) {
                    continue;
                }

                // Step the subtype keys, because the possibility exists that it's an
                // simple object with method name as key.
                for (const subMethod in payment.subtypes) {
                    // Does not contain a delayNotice information or is disabled.
                    if (!this.delayNoticeEnabled(payment.subtypes[subMethod])) {
                        continue;
                    }

                    return true;
                }
            }

            return false;
        },

        /**
         * Determine the current price of the order.
         *
         * @returns {Number}
         * */
        orderPrice() {
            const dunningFee = typeof this.order.dunning_fee === 'number' ? this.order.dunning_fee : 0;

            return this.price + dunningFee;
        },

        /**
         * Determine if the order has an active "auto-renew" flag enabled.
         *
         * @returns {boolean}
         * */
        isOrderWithAutoRenew() {
            return typeof this.order.auto_renew === 'boolean' && this.order.auto_renew;
        },
    },

    watch: {
        defaultCountry() {
            if (this.country !== null && this.country !== '') {
                return;
            }

            this.country = this.defaultCountry;
        },
        isPerformingRequest() {
            this.requestOngoing = this.isPerformingRequest;
        },
    },

    methods: {
        /**
         * Set the active payment method container.
         *
         * @param id
         *
         * @returns {*}
         */
        showPaymentMethod(id) {
            // eslint-disable-next-line vue/no-mutating-props
            return (this.activePaymentMethod = id);
        },

        /**
         * Handle the generation of form action link.
         *
         * @param {string} method
         *
         * @returns {string}
         * */
        generateFormActionLink(method) {
            return '/' + this.$language + '/backend/payment/start/' + method + '/' + this.order.type + '/' + this.order.id;
        },

        /**
         * Handle the submit of payment form, in the case of paypal the submit will be prevented.
         *
         * @param $event
         *
         * @returns {this}
         */
        onSubmit($event) {
            // We don't have a payment method or it's PayPal, in this case we should prevent
            // the default event if possible. Because PayPal will be initialized by Smart Buttons SDK.
            switch (this.method) {
                case null:
                    $event.preventDefault();
                    break;

                default:
                    this.requestOngoing = true;
                    break;
            }

            return this;
        },

        /**
         * Trigger the submit of form if required by the payment service itself.
         *
         * @param {MouseEvent|TouchEvent} $event
         * @param {Object} paymentMethod
         * @param {string|number|undefined} subMethod
         * @param {boolean|undefined} skipBillingDataModal
         *
         * @returns {void}
         */
        onPaymentOptionClick($event, paymentMethod, subMethod, skipBillingDataModal) {
            // In the case we have a preventable event.
            if ($event !== null) {
                $event.preventDefault();
            }

            // Check if we're allowed to skip the billing data modal.
            skipBillingDataModal = typeof skipBillingDataModal === 'boolean' && skipBillingDataModal;

            // Default
            let assignSubMethod = null;
            let delayNotice = null;
            let subtype = null;

            // In the case we got a subMethod value, we should inspect the payment method.
            if (
                typeof subMethod !== 'undefined' &&
                typeof paymentMethod.subtypes === 'object' &&
                paymentMethod.subtypes !== null &&
                typeof paymentMethod.subtypes[subMethod] === 'object'
            ) {
                subtype = paymentMethod.subtypes[subMethod];
                assignSubMethod = subtype.id;
                delayNotice = this.delayNoticeEnabled(subtype) ? subtype.delayNotice : null;
            }

            this.method = paymentMethod.id;
            this.subMethod = assignSubMethod;
            this.paymentNoticeModal.show = false;
            this.paymentNoticeModal.close = false;
            this.paymentNoticeModal.closeable = false;
            this.paymentNoticeModal.title = '';
            this.paymentNoticeModal.text = '';

            // Default status of modal visibility.
            this.askForBillingData = false;
            this.askForBillingDataDetails.paymentMethod = paymentMethod;
            this.askForBillingDataDetails.subtype = subtype;
            this.askForBillingDataDetails.subMethod = subMethod;

            // If the subtype requires an order we have to check, if the order contains all necessary
            // data like firstname, surname, street and house number to make sure we're able to generate
            // a order at the payment provider, which is needed to handle the payment process.
            if (
                !skipBillingDataModal &&
                subtype !== null &&
                typeof subtype.requiresOrder === 'boolean' &&
                subtype.requiresOrder
            ) {
                const mandatoryBillingData = {
                    givenName: this.order.first_name || this.order.paypal_first_name,
                    familyName: this.order.last_name || this.order.paypal_last_name,
                    street: this.order.street,
                    houseNumber: this.order.house,
                    postalCode: this.order.postcode,
                    city: this.order.city,
                    country: this.order.country,
                };

                // If the order is missing a required detail to create the order we have to show
                // the modal to enter the billing address details. Otherwise, we will restrict the
                // usage of this method.
                for (const property in mandatoryBillingData) {
                    // In the case the property contains a valid value, we don't need to ask the
                    // customer for his billing details.
                    if (
                        typeof mandatoryBillingData[property] === 'string' &&
                        mandatoryBillingData[property].trim().length > 0
                    ) {
                        continue;
                    }

                    // Otherwise, if one of these fields is missing we have to ask for the billing
                    // details. In this case we can end the execution of this loop directly.
                    this.askForBillingData = true;
                    break;
                }
            }

            if (this.askForBillingData) {
                return;
            }

            const handled = this.tryHandlePayment(paymentMethod);
            if (handled) {
                return;
            }

            // In the case the payment method force a delay notice to be visible, we should
            // show them to the user and wait for a confirmation to continue. Otherwise,
            // we will reset the payment form and give the user another option which he can chose.
            if (typeof delayNotice === 'object' && delayNotice !== null) {
                this.paymentNoticeModal.title = this.$t(delayNotice.title);
                this.paymentNoticeModal.text = this.$t(delayNotice.text);
                this.paymentNoticeModal.show = true;

                return;
            }

            this.$nextTick(() => {
                this.$refs.paymentForm.submit();
            });
        },

        tryHandlePayment(paymentMethod) {
            switch (paymentMethod.id) {
                case 'gift_voucher':
                    this.$refs.giftVoucherPaymentModal.onShowModal();
                    return true;
            }

            return false;
        },

        /**
         * Determine if the subtype has a active delay notice.
         *
         * @param {Object} subtype
         *
         * @returns {boolean}
         * */
        delayNoticeEnabled(subtype) {
            // We don't have delay notice definition.
            if (typeof subtype.delayNotice !== 'object' || subtype.delayNotice === null) {
                return false;
            }

            return !(typeof subtype.delayNotice.disabled === 'boolean' && subtype.delayNotice.disabled);
        },

        /**
         * Handle the callback of delay modal notice, to handle the selected action.
         *
         * @param {boolean} confirmed
         *
         * @returns {void}
         * */
        onDelayModalActionClick(confirmed) {
            // Switch the confirmation status of modal, because both will be handled
            // nearly the same. But in the case of canceled process, we will reset the
            // form values to prevent a submitting of this field.
            let callback = () => {};

            switch (confirmed) {
                case true:
                    callback = () => {
                        this.$refs.paymentForm.submit();
                    };

                    break;

                case false:
                default:
                    this.method = null;
                    this.subMethod = null;

                    break;
            }

            this.paymentNoticeModal.closeable = true;
            this.paymentNoticeModal.close = true;
            this.paymentNoticeModal.show = false;
            this.paymentNoticeModal.title = '';
            this.paymentNoticeModal.text = '';

            this.$nextTick(() => {
                this.paymentNoticeModal.closeable = false;
                this.paymentNoticeModal.close = false;
                callback();
            });
        },
    },

    mounted() {
        // We don't have an payment or it's not confirmed, we should set a method to use.
        // This normally will trigger the validate and create order request.
        if (!this.payment || !this.payment.is_confirmed) {
            this.method = this.paymentMethods[0].id;
        }
    },
};
</script>
<style lang="scss">
.order-payment-container {
    .payment-col:not(.disabled):not([disabled]) {
        cursor: pointer;
        margin-bottom: 1.25rem;
    }

    .payment-preview-container {
        overflow-x: hidden;
    }

    .payment-method-container {
        position: relative;

        .payment-icon:not(.payment-icon-image) {
            font-size: 48px;
        }

        .payment-icon.payment-icon-image {
            display: block;
            margin-top: -5px;
        }

        .payment-method-radio {
            position: absolute;
            top: 0;
            left: 0;
            z-index: 1;
            width: 1px;
            height: 1px;
            opacity: 0;

            &:checked + .payment-method-label:not(.without-check-icon) .payment-method-check-icon {
                background-color: #2f55d4;
                color: #fff;

                &::before {
                    display: inline-block;
                }
            }
        }

        .payment-method-label {
            position: relative;
            z-index: 2;
            padding: 16px 20px;

            &:not(.without-check-icon) {
                padding: 16px 20px 16px 52px;
            }

            &:not(.without-check-icon) .payment-method-check-icon {
                position: absolute;
                top: 50%;
                left: 0;
                transform: translateY(-50%);

                width: 2rem;
                height: 2rem;
                line-height: 2rem;
                border-radius: 50%;
                background-color: transparent;
                border: 1px solid #2f55d4;
                text-align: center;

                &::before {
                    display: none;
                    content: '\F12C';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translateX(-50%) translateY(-50%);
                    font: normal normal normal 24px/1 'Material Design Icons';
                    font-size: inherit;
                    text-rendering: auto;
                    line-height: inherit;
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                }
            }
        }
    }

    .customized-paypal-button-container {
        position: relative;

        .paypal-buttons-absolute {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1;
            opacity: 0;
        }
    }

    .paypal-payment-container {
        .form-group {
            margin-top: 1.25rem;
            margin-bottom: 0;
        }
    }

    .paypal-fixed-height {
        height: 62px;
    }
}
</style>
